interface PostAttachmentLike {
  media_type?: string;
  storage_url?: string;
  url?: string;
  type?: string;
}

interface PostLike {
  type?: string;
  url_content?: string | null;
  image_url?: string;
  thumbnail_url?: string;
  audio_url?: string;
  attachments?: PostAttachmentLike[];
}

// Platforms whose URLs must be embedded via <iframe> and cannot be played
// natively by the HTML <video> element.
const EMBED_URL_PATTERNS = [
  /youtube\.com\/embed\//i,
  /youtube\.com\/watch\?/i,
  /youtu\.be\//i,
  /vimeo\.com\//i,
  /dailymotion\.com\//i,
];

export function isEmbedUrl(url: string): boolean {
  return EMBED_URL_PATTERNS.some((pattern) => pattern.test(url));
}

function isDirectVideoUrl(url: string): boolean {
  // Accept common direct video/stream formats. Page URLs (e.g. x.com/.../status/...)
  // must not be treated as native media sources.
  return /\.(mp4|webm|ogg|mov|m3u8)(?:[?#]|$)/i.test(url);
}

export function isHlsSource(url: string, mimeType?: string): boolean {
  const normalizedType = mimeType?.split(';')[0].trim().toLowerCase();
  return /\.m3u8(?:[?#]|$)/i.test(url) ||
    normalizedType === 'application/x-mpegurl' ||
    normalizedType === 'application/vnd.apple.mpegurl';
}

export function resolveVideoMimeType(post: PostLike): string | undefined {
  const videoUrl = resolveVideoUrl(post);
  if (!videoUrl) return undefined;
  return post.attachments?.find(
    (attachment) => attachment.storage_url === videoUrl || attachment.url === videoUrl,
  )?.type;
}

function toYouTubeEmbedUrl(url: string): string | undefined {
  // Already an embed URL
  if (/youtube\.com\/embed\//.test(url)) return url;

  // youtube.com/watch?v=ID
  const watchMatch = /youtube\.com\/watch\?(?:.*&)?v=([^&#]+)/.exec(url);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  // youtu.be/ID
  const shortMatch = /youtu\.be\/([^?&#]+)/.exec(url);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  return undefined;
}

export function resolveEmbedUrl(post: PostLike): string | undefined {
  const candidate = post.url_content;
  if (!candidate) return undefined;
  if (!isEmbedUrl(candidate)) return undefined;

  if (/youtube\.com|youtu\.be/i.test(candidate)) {
    return toYouTubeEmbedUrl(candidate) ?? candidate;
  }

  return candidate;
}

export function resolveImageUrl(post: PostLike): string | undefined {
  const imageAttachment = post.attachments?.find(
    (a) => a.media_type === 'IMAGE' || a.type?.startsWith('image/'),
  );
  const candidate = imageAttachment?.storage_url ?? imageAttachment?.url ?? post.thumbnail_url ?? post.image_url;
  if (!candidate) return candidate;

  const matchingAttachment = post.attachments?.find(
    (attachment) => attachment.storage_url === candidate || attachment.url === candidate,
  );
  return isHlsSource(candidate, matchingAttachment?.type) ? undefined : candidate;
}

export function resolveAudioUrl(post: PostLike): string | undefined {
  const audioAttachment = post.attachments?.find(
    (a) => a.media_type === 'AUDIO' || a.type?.startsWith('audio/'),
  );
  return audioAttachment?.storage_url ?? audioAttachment?.url ?? post.audio_url ?? undefined;
}

function resolveVideoAttachmentUrl(attachment?: PostAttachmentLike): string | undefined {
  if (!attachment) return undefined;

  if (isHlsSource(attachment.url ?? attachment.storage_url ?? '', attachment.type)) {
    return attachment.url ?? attachment.storage_url;
  }

  return attachment.storage_url ?? attachment.url;
}

export function resolveVideoUrl(post: PostLike): string | undefined {
  const videoAttachment = post.attachments?.find(
    (a) => a.media_type === 'VIDEO' || a.type?.startsWith('video/'),
  );
  const attachmentUrl = resolveVideoAttachmentUrl(videoAttachment);

  if (post.type !== 'video' || !post.url_content) return attachmentUrl;
  if (isEmbedUrl(post.url_content)) return attachmentUrl;
  if (!isDirectVideoUrl(post.url_content)) return attachmentUrl;

  return post.url_content;
}
